import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, HardDrive } from 'lucide-react';
import toast from 'react-hot-toast';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { getOrganization, getOrganizationMembers, getOrganizationBugs, updatePlan, updateStorageLimit, updateStatus } from '../services/organizationService';
import { formatBytes } from '../utils/dateUtils';

const PLAN_BADGE: Record<string, 'default' | 'info' | 'success'> = { FREE: 'default', PRO: 'info', ENTERPRISE: 'success' };
const ROLE_BADGE: Record<string, 'warning' | 'info' | 'default'> = { OWNER: 'warning', ADMIN: 'info', MEMBER: 'default' };
const STATUS_BADGE: Record<string, 'error' | 'warning' | 'success'> = { OPEN: 'error', IN_PROGRESS: 'warning', RESOLVED: 'success' };

export default function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'org', id],
    queryFn: () => getOrganization(id!),
    enabled: !!id,
  });
  const { data: members } = useQuery({
    queryKey: ['admin', 'org', id, 'members'],
    queryFn: () => getOrganizationMembers(id!),
    enabled: !!id,
  });
  const { data: bugs } = useQuery({
    queryKey: ['admin', 'org', id, 'bugs'],
    queryFn: () => getOrganizationBugs(id!),
    enabled: !!id,
  });

  if (isLoading) return <div className="flex justify-center py-16"><Spinner className="size-8" /></div>;
  if (!data) return <p className="text-center text-gray-400 py-16">조직을 찾을 수 없습니다.</p>;

  const { organization: org, owner, stats } = data;
  const storageRawPct = stats.storageLimitBytes > 0 ? (stats.storageUsedBytes / stats.storageLimitBytes) * 100 : 0;
  const storagePct = Math.round(storageRawPct);
  const storagePctLabel = storageRawPct > 0 && storagePct === 0 ? '<1' : String(storagePct);
  const storageBarColor = storagePct >= 100 ? 'bg-red-500' : storagePct >= 80 ? 'bg-orange-500' : 'bg-primary-500';

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div>
        <button onClick={() => navigate('/organizations')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2">
          <ChevronLeft className="size-4" /> 조직 목록
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{org.name}</h1>
          <Badge variant={PLAN_BADGE[org.planType] ?? 'default'}>{org.planType}</Badge>
          <Badge variant={org.isActive ? 'success' : 'error'}>{org.isActive ? '활성' : '비활성'}</Badge>
        </div>
      </div>

      {/* 기본 정보 */}
      <Card title="기본 정보">
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Dl label="슬러그">{org.slug}</Dl>
          <Dl label="Owner">{owner ? `${owner.name} (${owner.email})` : '-'}</Dl>
          <Dl label="가입일">{new Date(org.createdAt).toLocaleDateString('ko-KR')}</Dl>
          <Dl label="플랜">{org.planType}</Dl>
          <Dl label="유료 전환일">{org.planStartedAt ? new Date(org.planStartedAt).toLocaleDateString('ko-KR') : '-'}</Dl>
          <Dl label="최근 활동">{org.lastActivityAt ? new Date(org.lastActivityAt).toLocaleString('ko-KR') : '-'}</Dl>
          <Dl label="멤버 수">{stats.memberCount}명</Dl>
          <Dl label="프로젝트 수">{stats.projectCount}개</Dl>
        </dl>
      </Card>

      {/* 멤버 목록 */}
      <Card title={`멤버 (${members?.length ?? 0})`}>
        {!members ? <Spinner className="size-5 mx-auto" /> : members.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">멤버가 없습니다.</p>
        ) : (
          <table className="w-full">
            <thead><tr className="border-b border-gray-100">
              <th className="pb-2 text-left text-sm font-semibold text-gray-500">이름</th>
              <th className="pb-2 text-left text-sm font-semibold text-gray-500">이메일</th>
              <th className="pb-2 text-left text-sm font-semibold text-gray-500">역할</th>
              <th className="pb-2 text-left text-sm font-semibold text-gray-500">합류일</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-50">
              {members.map((m) => (
                <tr key={m.id} className={m.isDeactivated ? 'opacity-50' : ''}>
                  <td className="py-2 text-sm text-gray-900">{m.user.name}</td>
                  <td className="py-2 text-sm text-gray-500">{m.user.email}</td>
                  <td className="py-2"><Badge variant={ROLE_BADGE[m.role] ?? 'default'}>{m.role}</Badge></td>
                  <td className="py-2 text-sm text-gray-500">{new Date(m.joinedAt).toLocaleDateString('ko-KR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* 스토리지 현황 */}
      <Card title="스토리지 현황">
        <div className="flex items-center gap-4">
          <HardDrive className="size-5 text-gray-400 shrink-0" />
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${storageBarColor}`} style={{ width: `${Math.max(storageRawPct > 0 ? 1 : 0, Math.min(storagePct, 100))}%` }} />
              </div>
              <span className="text-sm text-gray-600 font-medium">{storagePctLabel}%</span>
            </div>
            <p className="text-sm text-gray-500">{formatBytes(stats.storageUsedBytes)} / {formatBytes(stats.storageLimitBytes)}</p>
          </div>
        </div>
      </Card>

      {/* 오류 보고 */}
      <Card title="오류 보고">
        {!bugs ? <Spinner className="size-5 mx-auto" /> : bugs.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">오류 보고가 없습니다.</p>
        ) : (
          <>
            <div className="divide-y divide-gray-50">
              {bugs.slice(0, 5).map((b) => (
                <div key={b.id} onClick={() => navigate(`/bug-reports/${b.id}`)}
                  className="flex items-center justify-between py-2 hover:bg-gray-50 cursor-pointer -mx-5 px-5">
                  <span className="text-sm text-gray-900 truncate flex-1">{b.title}</span>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <Badge variant={STATUS_BADGE[b.status] ?? 'default'}>{b.status}</Badge>
                    <span className="text-sm text-gray-400">{new Date(b.createdAt).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}</span>
                  </div>
                </div>
              ))}
            </div>
            {bugs.length > 5 && (
              <button onClick={() => navigate(`/bug-reports?organizationId=${id}`)}
                className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium">
                전체 보기 →
              </button>
            )}
          </>
        )}
      </Card>

      {/* 운영 액션 */}
      <OpsActions orgId={id!} orgName={org.name} planType={org.planType}
        storageLimitBytes={stats.storageLimitBytes} storageUsedBytes={stats.storageUsedBytes}
        isActive={org.isActive} memberCount={stats.memberCount} />
    </div>
  );
}

function Dl({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900 font-medium mt-0.5">{children}</dd>
    </div>
  );
}

const PLAN_STORAGE_LABELS: Record<string, string> = { FREE: '2 GB', PRO: '10 GB', ENTERPRISE: '100 GB' };
const PLANS = ['FREE', 'PRO', 'ENTERPRISE'] as const;

function OpsActions({ orgId, orgName, planType, storageLimitBytes, storageUsedBytes, isActive, memberCount }: {
  orgId: string; orgName: string; planType: string;
  storageLimitBytes: number; storageUsedBytes: number;
  isActive: boolean; memberCount: number;
}) {
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState(planType);
  const [newLimitGB, setNewLimitGB] = useState(Math.round(storageLimitBytes / (1024 * 1024 * 1024) * 10) / 10);
  const [confirm, setConfirm] = useState<{ type: string; title: string; message: string; variant: 'default' | 'danger' } | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'org', orgId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
  };

  const planMutation = useMutation({
    mutationFn: () => updatePlan(orgId, selectedPlan),
    onSuccess: (data) => {
      toast.success('플랜이 변경되었습니다.');
      if ((data as Record<string, unknown>).warning) toast(String((data as Record<string, unknown>).warning), { icon: '⚠️' });
      invalidate();
      setConfirm(null);
    },
    onError: (e) => { toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '플랜 변경 실패'); setConfirm(null); },
  });

  const storageMutation = useMutation({
    mutationFn: () => updateStorageLimit(orgId, Math.round(newLimitGB * 1024 * 1024 * 1024)),
    onSuccess: () => { toast.success('스토리지 한도가 변경되었습니다.'); invalidate(); setConfirm(null); },
    onError: (e) => { toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '변경 실패'); setConfirm(null); },
  });

  const statusMutation = useMutation({
    mutationFn: () => updateStatus(orgId, !isActive),
    onSuccess: () => { toast.success(isActive ? '조직이 비활성화되었습니다.' : '조직이 활성화되었습니다.'); invalidate(); setConfirm(null); },
    onError: (e) => { toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '변경 실패'); setConfirm(null); },
  });

  const handleConfirm = () => {
    if (confirm?.type === 'plan') planMutation.mutate();
    else if (confirm?.type === 'storage') storageMutation.mutate();
    else if (confirm?.type === 'status') statusMutation.mutate();
  };

  return (
    <>
      <Card title="운영 액션">
        <div className="space-y-6">
          {/* 플랜 변경 */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">플랜 변경</h4>
            <div className="flex items-center gap-3">
              <Badge variant={PLAN_BADGE[planType] ?? 'default'}>{planType}</Badge>
              <span className="text-gray-400">→</span>
              <select value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500">
                {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <Button size="sm" disabled={selectedPlan === planType}
                onClick={() => setConfirm({
                  type: 'plan', title: '플랜 변경',
                  message: `${orgName}의 플랜을 ${planType}에서 ${selectedPlan}으로 변경하시겠습니까?\n스토리지 한도가 ${PLAN_STORAGE_LABELS[selectedPlan]}로 조정됩니다.`,
                  variant: 'default',
                })}>변경</Button>
            </div>
          </div>

          {/* 스토리지 한도 */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">스토리지 한도 조정</h4>
            <p className="text-sm text-gray-400 mb-2">현재: {formatBytes(storageLimitBytes)} / 사용량: {formatBytes(storageUsedBytes)} ({(() => { const r = storageLimitBytes > 0 ? (storageUsedBytes / storageLimitBytes * 100) : 0; const v = Math.round(r); return r > 0 && v === 0 ? '<1' : v; })()}%)</p>
            <div className="flex items-center gap-2">
              <input type="number" value={newLimitGB} onChange={(e) => setNewLimitGB(Number(e.target.value))}
                min={1} step={0.5}
                className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500" />
              <span className="text-sm text-gray-500">GB</span>
              <Button size="sm" onClick={() => setConfirm({
                type: 'storage', title: '스토리지 한도 조정',
                message: `${orgName}의 스토리지 한도를 ${newLimitGB} GB로 변경하시겠습니까?`,
                variant: 'default',
              })}>변경</Button>
            </div>
          </div>

          {/* 조직 상태 */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">조직 상태</h4>
            <div className="flex items-center gap-3">
              <Badge variant={isActive ? 'success' : 'error'}>{isActive ? '활성' : '비활성'}</Badge>
              {isActive ? (
                <Button size="sm" variant="danger" onClick={() => setConfirm({
                  type: 'status', title: '조직 비활성화', variant: 'danger',
                  message: `⚠️ ${orgName}을 비활성화하시겠습니까?\n모든 멤버(${memberCount}명)의 접근이 즉시 차단됩니다.`,
                })}>비활성화</Button>
              ) : (
                <Button size="sm" onClick={() => setConfirm({
                  type: 'status', title: '조직 활성화', variant: 'default',
                  message: `${orgName}을 다시 활성화하시겠습니까?`,
                })}>활성화</Button>
              )}
            </div>
            {!isActive && <p className="text-sm text-red-500 mt-1">모든 멤버의 접근이 차단된 상태입니다.</p>}
          </div>
        </div>
      </Card>

      <ConfirmDialog
        isOpen={!!confirm}
        title={confirm?.title ?? ''}
        message={confirm?.message ?? ''}
        variant={confirm?.variant ?? 'default'}
        confirmText="확인"
        loading={planMutation.isPending || storageMutation.isPending || statusMutation.isPending}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
}
